import {
  countryDisplayName,
  defaultAccountCopy,
  type PublicShippingDestination,
} from "@loot-os/api-contract";
import type { AccountShippingContent } from "@loot-os/design-system/templates/account/accountContent";

import { useCurrency } from "../../../../lib/currency.js";
import { addressLines, type useShippingAddress } from "../../../../lib/shippingAddress.js";
import { useShopper } from "../../../../lib/shopper.js";
import { useTerminology } from "../../../../lib/terminology.js";

/**
 * Settings → Shipping: GET /v1/me/shipping-address and where the store ships (enabled
 * destinations only). A store that ships nowhere says so instead of offering a form.
 */
export function useShippingContent({
  destinations,
  address,
  locked,
  openAddress,
}: {
  destinations: readonly PublicShippingDestination[];
  address: ReturnType<typeof useShippingAddress>;
  locked: boolean;
  openAddress: () => void;
}): AccountShippingContent {
  const { sessionToken } = useShopper();
  const currency = useCurrency();
  const copy = defaultAccountCopy(useTerminology()).shipping;
  const saved = address.data?.shippingAddress ?? null;
  return {
    title: copy.title,
    description: copy.description,
    currency,
    address:
      address.isPending && sessionToken
        ? { status: "loading" }
        : saved
          ? { status: "saved", lines: addressLines(saved) }
          : { status: "none", message: copy.noAddress },
    edit:
      destinations.length > 0
        ? {
            label: saved ? copy.edit : copy.add,
            emphasis: saved ? "secondary" : "primary",
            disabled: locked || address.isPending,
            onClick: openAddress,
          }
        : null,
    destinations:
      destinations.length === 0
        ? { status: "unavailable", message: copy.unavailable }
        : {
            status: "ready",
            title: copy.destinations.title,
            columns: copy.destinations.columns,
            freeLabel: copy.destinations.free,
            rows: destinations.map((d) => ({
              code: d.countryCode,
              country: countryDisplayName(d.countryCode),
              costCents: d.costCents,
              delivery: d.deliveryEstimate,
              current: saved?.country === d.countryCode,
            })),
          },
  };
}
